import React from 'react'
import { useParams } from 'react-router'
import { PageHeader } from '@mobi/component-library/Navigation/PageHeader'
import { AdditionalDetails } from './Components/AdditionalDetails'
import { AddressAutocomplete } from './AddressAutocomplete'
import { BetAccountKey, BetAccountRecord, BetAccountRepo } from '@core/Data/Account/betAccount'
import { ButtonBlock } from '@mobi/component-library/Common/Buttons'
import { CheckboxInput } from '@mobi/component-library/Common/Input/CheckboxInput'
import { connect } from '../../Components/HOCs/connect'
import { ContactDetailsReverificationSuccess } from '../Reverification/Components/ReverificationComplete'
import { CustomerServiceTelephone } from '@mobi/component-library/Common/V2'
import { DesktopSpinner, Spinner } from '@mobi/component-library/Common/Spinner'
import { ErroredInstance, isErrored, isLoading, LoadingInstance } from 'rwwa-data-access'
import { ErrorMessage } from '../../Components/Messages'
import { FailedSaveMessage } from './Components/FailedSaveMessage/FailedSaveMessage'
import { getContactDetailsFields } from './data-transforms'
import { getFromRepo } from '@core/Utils/repository'
import { Grid, GridCell } from '@mobi/component-library/Common/Grid'
import { MainContainer } from '../../Components/Containers'
import { ManualAddressEntry } from './Components/ManualAddressEntry'
import { NameDateOfBirthChangedNotice } from './Components/NameDateOfBirthChangedNotice/NameDateOfBirthChangedNotice'
import { appHistory } from '@core/Utils/routing/history'
import { observeImmutable } from '../../Components/HOCs/observe'
import { Reset } from '@core/Areas/ContactDetails/driver'
import { useBetAccount } from '@mobi/account/Areas/Deposit/Hooks'
import { useLogon } from '@core/Utils/hooks'
import {
  ContactDetails as ContactDetailsRepo,
  ContactDetailsKey,
} from '../../Data/Account/contactDetails'
import type { BetAccountHolder } from '@mobi/api-types'
import {
  ShowManualAddressForm,
  ShowManualPostalAddressForm,
} from './Components/ManualAddressEntry/driver'
import {
  state$,
  type ContactDetailsFields,
  type ContactDetailsStateRecord,
  ChangeValue,
  ChangeResidentialAddress,
  ChangePostalAddress,
  UpdateFields,
  SaveButtonClicked,
  type ContactDetailsState,
  isFieldValidationSuccess,
} from './driver'
import {
  ContactDetailsContainer,
  ContactDetailsPreferredNameStyled,
  ContactDetailsItemStyled,
  ContactDetailsTitleLabelStyled,
  ContactDetailsEditLabelStyled,
  ContactDetailsManualAddressButton,
} from './ContactDetails.styles'

export interface ContactEditDetailsProps {
  isDesktop?: boolean
  betAccountHolderNumber?: string | null
  navigateOnUpdateSuccess: () => void
}

export const ContactDetailsEditMobiWrapper = () => {
  const { accountNumber } = useParams<{ accountNumber?: string }>()

  return (
    <ContactDetailsEdit
      isDesktop={false}
      betAccountHolderNumber={accountNumber}
      navigateOnUpdateSuccess={() => appHistory.push('/account/contactdetails')}
    />
  )
}

export const ContactDetailsEdit = ({
  isDesktop,
  betAccountHolderNumber,
  navigateOnUpdateSuccess,
}: ContactEditDetailsProps) => {
  return (
    <ConnectContactDetails
      contactDetails={betAccountHolderNumber || ContactDetailsKey}
      isDesktop={isDesktop}
      navigateOnUpdateSuccess={navigateOnUpdateSuccess}
    />
  )
}

export const ContactDetailsEditLoader = ({
  contactDetails,
  isDesktop,
  navigateOnUpdateSuccess,
}: {
  contactDetails: typeof LoadingInstance | typeof ErroredInstance | BetAccountHolder
} & ContactEditDetailsProps) => {
  const { accountNumber, isLoggedIn } = useLogon()
  const betAccountQuery = useBetAccount<BetAccountRecord>({
    enabled: accountNumber !== null && isLoggedIn === true,
    queryFn: () => getFromRepo(BetAccountRepo, BetAccountKey),
  })

  React.useEffect(() => Reset(), [])

  if (isLoading(contactDetails) || betAccountQuery.isLoading) {
    return isDesktop ? <DesktopSpinner /> : <Spinner />
  }

  if (isErrored(contactDetails) || betAccountQuery.isError) {
    return <ErrorMessage>An error occurred while loading your contact details.</ErrorMessage>
  }

  // Catch non-verified users
  if (betAccountQuery.data?.IsIDVerified === false) {
    return null
  }

  const props = getContactDetailsFields(contactDetails)
  return (
    <ContactDetailsEditSetup
      {...props}
      isDesktop={isDesktop}
      navigateOnUpdateSuccess={navigateOnUpdateSuccess}
    />
  )
}

const ConnectContactDetails = connect({
  contactDetails: ContactDetailsRepo,
})<ContactEditDetailsProps>(ContactDetailsEditLoader)

type ContactDetailsEditSetupProps = ContactDetailsFields &
  Pick<ContactEditDetailsProps, 'isDesktop' | 'navigateOnUpdateSuccess'>

class ContactDetailsEditSetup extends React.Component<ContactDetailsEditSetupProps> {
  public componentDidMount() {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { isDesktop, navigateOnUpdateSuccess, ...fields } = this.props
    UpdateFields(fields)
  }

  public render() {
    const { isDesktop, navigateOnUpdateSuccess } = this.props
    return (
      <ContactDetailsEditObserver
        isDesktop={isDesktop}
        navigateOnUpdateSuccess={navigateOnUpdateSuccess}
      />
    )
  }
}

const ContactDetailsEditObserver = observeImmutable<
  ContactDetailsStateRecord,
  Pick<ContactEditDetailsProps, 'isDesktop'> &
    Pick<ContactEditDetailsProps, 'navigateOnUpdateSuccess'>
>(state$, ({ record, isDesktop, navigateOnUpdateSuccess }) => {
  const state: ContactDetailsState = record.toJS()

  if (state.updateSuccess) {
    navigateOnUpdateSuccess()
    return null
  }

  return <ContactDetailsEditComponent {...state} isDesktop={isDesktop} />
})

const changeAddressResidential = (value: string) =>
  ChangeResidentialAddress({ addressResidentialMoniker: value })
const changeAddressPostal = (value: string) => ChangePostalAddress({ addressPostalMoniker: value })
const changeAddressPostalSame = (event: React.FormEvent<HTMLInputElement>) =>
  ChangeValue({ field: 'addressPostalSame', value: event.currentTarget.checked })
const changeNameDateOfBirthChanged = (event: React.FormEvent<HTMLInputElement>) =>
  ChangeValue({ field: 'nameDateOfBirthChanged', value: event.currentTarget.checked })

export const ContactDetailsEditComponent = ({
  fullName,
  preferredName,
  addressResidentialMoniker,
  addressPostalMoniker,
  addressPostalSame,
  validationErrors,
  updateFailed,
  loading,
  isAdditionalDetailsChanged,
  agreeToVerifyAdditionalDetails,
  isDesktop,
  nameDateOfBirthChanged,
  nameDateOfBirthChangedInitial,
  isInternationalCustomer,
}: ContactDetailsState & Pick<ContactEditDetailsProps, 'isDesktop'>) => {
  const fieldValidationSucceeded = isFieldValidationSuccess(
    addressPostalSame,
    isAdditionalDetailsChanged,
    !!agreeToVerifyAdditionalDetails,
    validationErrors
  )

  const handleCancel = () => {
    if (isDesktop) {
      window.history.back()
    } else {
      appHistory.back()
    }
  }

  return (
    <MainContainer>
      <ContactDetailsReverificationSuccess isDesktop={!!isDesktop} navigateToHistoryBack={true} />

      {!isDesktop && <PageHeader title='Edit Contact Details' />}

      <ContactDetailsContainer>
        {preferredName && (
          <ContactDetailsPreferredNameStyled>{preferredName}</ContactDetailsPreferredNameStyled>
        )}
        <ContactDetailsItemStyled>{fullName}</ContactDetailsItemStyled>

        <form>
          <fieldset>
            {isInternationalCustomer ? (
              <ReadonlyAddress
                addressResidentialMoniker={addressResidentialMoniker}
                addressPostalMoniker={addressPostalMoniker}
                addressPostalSame={addressPostalSame}
              />
            ) : (
              <EditableAddress
                addressResidentialMoniker={addressResidentialMoniker}
                addressPostalMoniker={addressPostalMoniker}
                addressPostalSame={addressPostalSame}
                validationErrors={validationErrors}
              />
            )}

            {isInternationalCustomer && (
              <CheckboxInput
                id='contact-centre-checkbox'
                data-tid-contact-centre-checkbox=''
                checked={nameDateOfBirthChanged}
                disabled={nameDateOfBirthChangedInitial}
                onChange={changeNameDateOfBirthChanged}
                label={
                  <>
                    For international address changes please select the tick box and a customer
                    representative will contact you. Alternatively call{' '}
                    <CustomerServiceTelephone isInternational={isInternationalCustomer} />.
                  </>
                }
              />
            )}
          </fieldset>
        </form>

        <ManualAddressEntry />

        <NameDateOfBirthChangedNotice />

        <AdditionalDetails />

        <Grid padding='0.25rem'>
          <GridCell data-tid-cancel-button=''>
            <ButtonBlock color='secondary' onClick={handleCancel}>
              Cancel
            </ButtonBlock>
          </GridCell>

          <GridCell data-tid-save-button=''>
            <ButtonBlock
              onClick={SaveButtonClicked}
              disabled={!fieldValidationSucceeded || loading}
            >
              Save
            </ButtonBlock>

            {loading ? <Spinner /> : null}
          </GridCell>
        </Grid>
        <FailedSaveMessage showFailedMessage={updateFailed} />
      </ContactDetailsContainer>
    </MainContainer>
  )
}

const ReadonlyAddress = ({
  addressPostalSame,
  addressResidentialMoniker,
  addressPostalMoniker,
}: Pick<
  ContactDetailsState,
  'addressPostalSame' | 'addressResidentialMoniker' | 'addressPostalMoniker'
>) => {
  return (
    <div>
      <ContactDetailsTitleLabelStyled>Residential Address</ContactDetailsTitleLabelStyled>
      <ContactDetailsItemStyled>{addressResidentialMoniker}</ContactDetailsItemStyled>

      {!addressPostalSame && (
        <ContactDetailsTitleLabelStyled>Postal Address</ContactDetailsTitleLabelStyled>
      )}
      {!addressPostalSame && (
        <ContactDetailsItemStyled>{addressPostalMoniker}</ContactDetailsItemStyled>
      )}
    </div>
  )
}

const EditableAddress = ({
  addressPostalSame,
  addressResidentialMoniker,
  addressPostalMoniker,
  validationErrors,
}: Pick<
  ContactDetailsState,
  'addressPostalSame' | 'addressResidentialMoniker' | 'addressPostalMoniker' | 'validationErrors'
>) => {
  return (
    <div>
      <ContactDetailsManualAddressButton type='button' onClick={ShowManualAddressForm}>
        Manually Enter Address
      </ContactDetailsManualAddressButton>
      <ContactDetailsEditLabelStyled htmlFor='contact-address'>
        Residential Address<span>*</span>
      </ContactDetailsEditLabelStyled>

      <AddressAutocomplete
        id='contact-address-autocomplete'
        inputId='contact-address'
        value={addressResidentialMoniker}
        onSelection={changeAddressResidential}
        errorMessage={validationErrors.addressResidentialMoniker}
      />

      <CheckboxInput
        id='postal-address-checkbox'
        data-tid-postal-address-checkbox=''
        checked={addressPostalSame}
        onChange={changeAddressPostalSame}
        label='This is my postal address.'
      />

      {addressPostalSame ? null : (
        <ContactDetailsManualAddressButton type='button' onClick={ShowManualPostalAddressForm}>
          Manually Enter Address
        </ContactDetailsManualAddressButton>
      )}
      {addressPostalSame ? null : (
        <ContactDetailsEditLabelStyled htmlFor='contact-address-postal'>
          Postal address<span>*</span>
        </ContactDetailsEditLabelStyled>
      )}
      {addressPostalSame ? null : (
        <AddressAutocomplete
          id='contact-address-postal-autocomplete'
          inputId='contact-address-postal'
          value={addressPostalMoniker}
          onSelection={changeAddressPostal}
          autoFocus={!!validationErrors.addressPostalMoniker}
          errorMessage={validationErrors.addressPostalMoniker}
        />
      )}
    </div>
  )
}
